import React, { useEffect, useId, useRef } from 'react';
import { AmplifierModalPortal } from './AmplifierModalPortal';
import { useAmplifierI18n } from '../../i18n/amplifier/useAmplifierI18n';

export interface AntennaNameEditModalProps {
  open: boolean;
  defaultName: string;
  nameValue: string;
  freqStartHz: string;
  freqEndHz: string;
  onNameChange: (value: string) => void;
  onFreqStartChange: (value: string) => void;
  onFreqEndChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  saving?: boolean;
}

/** 长按天线标签后弹出的配置编辑框（协议 §5.6.3） */
export const AntennaNameEditModal: React.FC<AntennaNameEditModalProps> = ({
  open,
  defaultName,
  nameValue,
  freqStartHz,
  freqEndHz,
  onNameChange,
  onFreqStartChange,
  onFreqEndChange,
  onSave,
  onCancel,
  saving = false,
}) => {
  const { t } = useAmplifierI18n();
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [open]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSave();
  };

  return (
    <AmplifierModalPortal open={open} onBackdropClick={saving ? undefined : onCancel}>
      <form
        className="amplifier-modal-panel antenna-name-edit-modal antenna-config-edit-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${inputId}-title`}
        onSubmit={handleSubmit}
        onClick={(event) => event.stopPropagation()}
      >
        <h3 id={`${inputId}-title`} className="amplifier-modal-panel__title">
          {t('修改天线配置')}
        </h3>
        <p className="amplifier-modal-panel__hint">
          {t('默认名称：{name}。名称留空并保存可恢复默认显示。频率单位为 Hz。', { name: defaultName })}
        </p>
        <label className="amp-modal-field-label" htmlFor={`${inputId}-name`}>
          {t('自定义名称')}
        </label>
        <input
          id={`${inputId}-name`}
          ref={inputRef}
          type="text"
          className="amp-modal-input"
          value={nameValue}
          maxLength={32}
          placeholder={defaultName}
          disabled={saving}
          onChange={(event) => onNameChange(event.target.value)}
        />
        <div className="antenna-config-edit-modal__freq-row">
          <div className="antenna-config-edit-modal__freq-field">
            <label className="amp-modal-field-label" htmlFor={`${inputId}-freq-start`}>
              {t('开始频率 (Hz)')}
            </label>
            <input
              id={`${inputId}-freq-start`}
              type="text"
              inputMode="numeric"
              className="amp-modal-input"
              value={freqStartHz}
              placeholder="1800000"
              disabled={saving}
              onChange={(event) => onFreqStartChange(event.target.value)}
            />
          </div>
          <div className="antenna-config-edit-modal__freq-field">
            <label className="amp-modal-field-label" htmlFor={`${inputId}-freq-end`}>
              {t('结束频率 (Hz)')}
            </label>
            <input
              id={`${inputId}-freq-end`}
              type="text"
              inputMode="numeric"
              className="amp-modal-input"
              value={freqEndHz}
              placeholder="130000000"
              disabled={saving}
              onChange={(event) => onFreqEndChange(event.target.value)}
            />
          </div>
        </div>
        <div className="antenna-name-edit-modal__actions">
          <button type="button" className="amp-modal-btn" disabled={saving} onClick={onCancel}>
            <span className="amp-modal-btn__text">{t('取消')}</span>
          </button>
          <button type="submit" className="amp-modal-btn amp-modal-btn--primary" disabled={saving}>
            <span className="amp-modal-btn__text">{saving ? t('保存中…') : t('保存')}</span>
          </button>
        </div>
      </form>
    </AmplifierModalPortal>
  );
};

export default AntennaNameEditModal;
